import { StringUtils, Utils } from "neo-common";
import { Communication } from "neo-games";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../../../Utils/FrameTimer";
import { GsapEffects } from "../../../Components/Effects/GsapEffects";
import { TweenLite } from "gsap";
import { Languages } from "../../../Utils/Languages";
import { AppContext } from "../../../../Common/AppContext";
import { Tweens } from "../../../Components/Effects/Tweens";

type IProgressiveBonusLevel = Communication.Game.IProgressiveBonusLevel;

export class JackpotCounter {
    //******************************************************************************************************************
    //  SEETTINGS
    //******************************************************************************************************************

    private static LeadingZeroLetter: string = " ";

    private static WinAnimDuration: number = 240; //in frames

    private static WinFromMinigameAnimDuration: number = 540; //in frames

    private static Length: number = 10;

    private static DecimalPlaces: number = 2;

    private static DigitAnimDuration: number = 0.1; //in seconds

    //could be in conflict with priority assign (if reset animations haven't been finished)
    private static MinResetAnimCount: number = 15;

    private static UseThousandsSeparator: boolean = true;

    private static MaxNextTimeDiff: number = 2 * 60; //in seconds

    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    protected root: HTMLElement;

    protected currentData: IProgressiveBonusLevel;

    protected currentCounterValue: number;

    protected previousCounterValue: number;

    protected nextDate: number;

    protected nextValue: number;

    protected previousValue: number;

    protected lastWinValue: number;

    protected lastWinCount: number;

    protected overridedLastWinValue: number;

    protected serverTimeOffset: number;

    protected resetAnimCount: number;

    protected isCounting: boolean;

    protected inError: boolean;

    protected winFromMinigame: boolean;

    protected winAnimationDelay: FrameTimerEventEmitter;

    protected mainTween: TweenLite;

    protected digitTween: TweenLite;

    protected decimalDot: HTMLElement;

    protected idleValue: number;

    protected lastUpdateTime: number;

    protected resetCallback: Function;

    protected digits: {
        from: HTMLElement;
        to: HTMLElement;
    }[];

    protected settings: {
        width: number;
        height: number;
    };

    //******************************************************************************************************************
    //  CONSTRUCTOR
    //******************************************************************************************************************

    constructor(index: number, holder: HTMLElement, resetCallback: Function) {
        if (AppContext.gameConfiguration != null) {
            JackpotCounter.DecimalPlaces = AppContext.gameConfiguration.ShowDecimalPlaces;
        }

        this.root = this.createBaseLayout(index);
        holder.appendChild(this.root);
        this.createDigits();
        this.createDecimalPoint();

        this.currentCounterValue = 0;
        this.previousCounterValue = 0;
        this.lastWinValue = -1;
        this.lastWinCount = -1;
        this.overridedLastWinValue = -1;

        this.winAnimationDelay = CreateFrameTimer("winAnimationDelayTimer");
        this.winAnimationDelay.on(FrameTimerEvents.Complete, this.winAnimationFinished.bind(this));

        this.mainTween = null;
        this.digitTween = null;
        this.winFromMinigame = false;

        this.lastUpdateTime = Date.now();
        this.resetCallback = resetCallback;

        this.reset();
    }

    //******************************************************************************************************************
    //  CREATE
    //******************************************************************************************************************

    protected createBaseLayout(index: number): HTMLElement {
        let root = this.createElement("div", "jackpotCounter l-" + index);

        //create childs
        this.createElement("div", "jackpotFrame", root);
        let level = this.createElement("div", "jackpotLevel", root);
        this.createElement("div", "jackpotIconError", level);

        this.createElement("div", "jackpotDigitsHolder", root);
        this.createElement("div", "jackpotGradient", root);
        let currency = this.createElement("div", "jackpotCurrency", root);

        //create currency letters
        this.createElement("div", "jackpotCurrencyLetter", currency);
        this.createElement("div", "jackpotCurrencyLetter", currency);
        this.createElement("div", "jackpotCurrencyLetter", currency);

        return root;
    }

    public createCurrency(currency: string): void {
        if (!StringUtils.isNullOrEmpty(currency)) {
            let jackpotCurrencyLetters = this.root.getElementsByClassName("jackpotCurrencyLetter");

            for (let i = 0; i < jackpotCurrencyLetters.length; i++) {
                if (currency.length > i) {
                    jackpotCurrencyLetters[i].textContent = currency.charAt(i);
                }
            }

            jackpotCurrencyLetters[0].parentElement.classList.add("ln-" + currency.length);
        }
    }

    protected createDigits(): void {
        let digitsHolder = <HTMLElement>this.root.getElementsByClassName("jackpotDigitsHolder")[0];
        const digitsArray: Array<HTMLElement> = [];
        this.digits = [];

        let digit: HTMLElement;
        for (let i = 0; i < JackpotCounter.Length; i++) {
            let className =
                i == JackpotCounter.Length - JackpotCounter.DecimalPlaces ? "digits decimalOffset" : "digits";
            digit = this.createElement("div", className, digitsHolder);
            let from = this.createElement("div", "digit", digit);
            let to = this.createElement("div", "digit", digit);

            digitsArray.push(digit);
            this.digits.push({ from: from, to: to });
        }

        if (JackpotCounter.UseThousandsSeparator) {
            digitsHolder.classList.add("thousandsFormat");

            for (let i = 0; i < digitsArray.length; i++) {
                const index = digitsArray.length - 1 - i;
                const hasThousandsOffset =
                    index > 0 && i > JackpotCounter.DecimalPlaces && (i + 1 - JackpotCounter.DecimalPlaces) % 3 == 0;

                if (hasThousandsOffset && digitsArray[index] != null) {
                    digitsArray[index].classList.add("thousandsOffset");
                }
            }
        }

        //save digit height/width
        this.settings = {
            width: parseInt(window.getComputedStyle(digit).width),
            height: parseInt(window.getComputedStyle(digit).height),
        };
    }

    protected createDecimalPoint() {
        let digitsHolder = <HTMLElement>this.root.getElementsByClassName("jackpotDigitsHolder")[0];

        if (JackpotCounter.DecimalPlaces > 0) {
            this.decimalDot = this.createElement("div", "digit nDot", digitsHolder);
            this.decimalDot.style.right = JackpotCounter.DecimalPlaces * this.settings.width + "px";
        }
    }

    //******************************************************************************************************************
    //  INIT
    //******************************************************************************************************************

    public initialize(data: IProgressiveBonusLevel, serverTimestamp: string): void {
        this.assign(data, serverTimestamp);
    }

    //******************************************************************************************************************
    //  ASSIGN
    //******************************************************************************************************************

    public assign(
        data: IProgressiveBonusLevel,
        serverTimestamp: string,
        priority: boolean = false,
        overridedLastWinValue: number = -1,
        winFromMinigame: boolean = false,
    ): void {
        this.currentData = data;

        this.nextValue = this.normalizeValue(data.nextValue);
        this.nextDate = Date.now() + (new Date(data.nextTimestamp).getTime() - new Date(serverTimestamp).getTime());
        this.previousValue = this.normalizeValue(data.previousValue);
        this.winFromMinigame = winFromMinigame;

        const oldValuesReset = Date.now() - this.lastUpdateTime > JackpotCounter.MaxNextTimeDiff * 1000;

        if (overridedLastWinValue > -1) {
            this.overridedLastWinValue = this.normalizeValue(overridedLastWinValue);
        }

        //if current animated values are too old, force reset animation on all counters
        if (oldValuesReset && this.resetCallback != null) {
            this.resetCallback();
            return;
        }

        //reset counter if it's in error
        if (this.inError) {
            this.removeError();
            this.reset();
            return;
        }

        if (!this.isCounting || priority) {
            this.refresh();
        }
    }

    //******************************************************************************************************************
    //  ERROR
    //******************************************************************************************************************

    public error(useResetAnim: boolean = false): void {
        if (!this.inError) {
            this.inError = true;
            this.isCounting = false;

            if (useResetAnim) {
                this.resetMainTween();
                this.resetDigitTween(true);
                this.errorResetAnim();
                return;
            }

            if (this.decimalDot) {
                this.decimalDot.style.display = "none";
            }

            this.root.classList.add("jackpotError");
            const oldValue = this.formatValue(
                this.previousCounterValue > 0 ? this.previousCounterValue.toString() : "",
                true,
            );
            const newValue = this.formatValue("OFFLINE", true);

            this.resetDigitTween(true);
            this.animateDigits(
                oldValue,
                newValue,
                false,
                false,
                JackpotCounter.DigitAnimDuration * 2,
                0,
                "linear",
                false,
                null,
            );
        }
    }

    private errorResetAnim(): void {
        this.root.classList.add("jackpotExpired");
    }

    private checkError(): boolean {
        if (this.inError) {
            this.resetMainTween();
            this.resetDigitTween();
            this.isCounting = false;

            return true;
        }

        return false;
    }

    private removeError(): void {
        if (this.inError) {
            this.inError = false;

            this.root.classList.remove("jackpotError");
            if (this.decimalDot) {
                this.decimalDot.style.display = "block";
            }
        }
    }

    public hasError(): boolean {
        return this.inError;
    }

    public refreshWithReset(): void {
        this.hasWin(); //update last wins to not showing old wins
        this.root.classList.remove("jackpotWin");
        this.winAnimationDelay.stop();
        this.resetMainTween();
        this.resetDigitTween();
        this.resetDigitsTween();
        this.isCounting = false;
        this.removeError();
        this.reset(JackpotCounter.MinResetAnimCount - 3);
    }

    private resetDigitsTween(): void {
        for (let i = 0; i < this.digits.length; i++) {
            TweenLite.killTweensOf(this.digits[i].to);
            TweenLite.killTweensOf(this.digits[i].from);
        }
    }

    //******************************************************************************************************************
    //  COUNTING
    //******************************************************************************************************************

    protected animateDigits(
        oldValue: string,
        newValue: string,
        useBitmap: boolean,
        checkFirstDiff: boolean,
        duration: number,
        delay: number,
        ease: string,
        shuffle: boolean,
        callback: () => void,
    ): void {
        let firstDigitIndex = 0;
        if (checkFirstDiff) {
            for (let i = 0; i < JackpotCounter.Length; i++) {
                const oldDigit = oldValue.charAt(i);
                const newDigit = newValue.charAt(i);

                if (oldDigit !== newDigit) {
                    firstDigitIndex = i;
                    break;
                }
            }
        }

        for (let i = firstDigitIndex; i < JackpotCounter.Length; i++) {
            const oldDigit = oldValue.charAt(i);
            const newDigit = newValue.charAt(i);

            this.animateDigit(
                i,
                oldDigit,
                newDigit,
                duration,
                delay,
                ease,
                useBitmap,
                shuffle ? (i % 2 === 0 ? 1 : -1) : 1,
                i === JackpotCounter.Length - 1 ? callback : null,
            );
        }
    }

    protected animateDigit(
        index: number,
        fromValue: string,
        toValue: string,
        duration: number,
        delay: number,
        ease: string,
        useBitmap: boolean,
        direction: number,
        callback: () => void,
    ): void {
        const from = this.digits[index].from;
        this.setTransformY(from, 0, delay);
        this.setDigitValue(from, fromValue, useBitmap, true);
        TweenLite.fromTo(
            from,
            duration,
            { y: 0 },
            { y: this.settings.height * direction * -1, ease: ease, delay: delay },
        );

        const to = this.digits[index].to;
        this.setTransformY(to, this.settings.height * direction, delay);
        this.setDigitValue(to, toValue, useBitmap);
        TweenLite.killTweensOf(to); //prevent tween from bad finishing, progress stop at 0.8 and don't emit onComplete
        let tween = TweenLite.fromTo(
            to,
            duration,
            { y: this.settings.height * direction },
            { y: 0, ease: ease, delay: delay },
        );

        if (!Utils.isNullOrUndefined(callback)) {
            this.digitTween = tween;
            this.digitTween.eventCallback("onComplete", callback, [], this);
        }
    }

    protected setDigitValue(elem: HTMLElement, value: string, useBitmap: boolean, fromValue: boolean = false): void {
        if (fromValue) {
            if (useBitmap && elem.classList.contains("letter")) {
                useBitmap = false;
            } else if (!useBitmap && !elem.classList.contains("letter")) {
                useBitmap = true;
            }
        }

        if (value == null || value === JackpotCounter.LeadingZeroLetter) {
            elem.className = "digit leadingZero";
        } else {
            if (!useBitmap) {
                elem.textContent = value;
                elem.className = "digit letter";
            } else {
                elem.textContent = "";
                elem.className = "digit n" + value;
            }
        }
    }

    protected setTransformY(elem: HTMLElement, y: number, delay: number): void {
        if (delay > 0) {
            elem.style.transform = "transform3d(0, " + y + "px, 0)";
        }
    }

    //******************************************************************************************************************
    //  REFRESH & RESET
    //******************************************************************************************************************

    private refresh() {
        if (this.checkError()) {
            return;
        }

        //if there where win
        if (this.hasWin()) {
            this.isCounting = true;
            this.initWinAnimation();
            return;
        }

        //if there is no new nextValue
        if (this.currentCounterValue == this.nextValue) {
            this.resetDigitTween();
            this.resetDigitsToBasePosition();
            this.lastUpdateTime = Date.now();

            //run empty tween do handle idle state
            const availableTime = (this.nextDate - Date.now()) * 0.001;
            if (availableTime > 0) {
                this.resetMainTween();
                this.mainTween = TweenLite.fromTo(
                    this,
                    availableTime,
                    { idleValue: 0 },
                    { idleValue: 100, ease: "linear" },
                );
                this.mainTween.eventCallback("onComplete", this.refresh, [], this);
            } else {
                this.isCounting = false;
            }

            return;
        }

        //if new value is lower then current value
        if (this.currentCounterValue > this.nextValue) {
            this.isCounting = false;
            return;
        }

        const availableTime = (this.nextDate - Date.now()) * 0.001;
        this.resetMainTween();
        this.mainTween = TweenLite.fromTo(
            this,
            availableTime,
            { currentCounterValue: this.currentCounterValue },
            { currentCounterValue: this.nextValue, ease: "linear", roundProps: "currentCounterValue" },
        );
        this.mainTween.eventCallback("onComplete", this.refresh, [], this);
        this.mainTween.eventCallback("onUpdate", this.onRefreshTweenUpdate, [], this);

        this.isCounting = true;
    }

    private onRefreshTweenUpdate(): void {
        if (this.digitTween == null) {
            this.refreshCounter();
        }
    }

    private refreshCounter(): void {
        if (this.inError) {
            this.resetDigitTween();
            return;
        }

        if (this.previousCounterValue == this.currentCounterValue) {
            this.resetDigitTween();
            return;
        }

        const oldValue = this.formatValue(this.previousCounterValue.toString(), true);
        const newValue = this.formatValue(this.currentCounterValue.toString(), true);

        this.lastUpdateTime = Date.now();

        this.animateDigits(
            oldValue,
            newValue,
            true,
            true,
            JackpotCounter.DigitAnimDuration * 2,
            0,
            "linear",
            false,
            this.refreshCounter,
        );
        this.previousCounterValue = this.currentCounterValue;
    }

    private reset(resetAnimCount: number = 0): void {
        this.resetAnimCount = resetAnimCount;
        this.isCounting = true;

        this.resetCounter();
    }

    private resetCounter(): void {
        if (this.checkError()) {
            return;
        }

        if (this.resetAnimCount >= JackpotCounter.MinResetAnimCount - 1 && this.currentData != null) {
            //if we have win right after reset
            if (this.hasWin()) {
                this.resetDigitTween(); //we need to reset digit tween to allow win anim start during reset anim
                this.isCounting = true;
                this.initWinAnimation();
                return;
            }
            const oldValue = this.formatValue(this.currentCounterValue.toString(), false);
            const newValue = this.formatValue(this.previousValue.toString(), true);

            this.currentCounterValue = this.previousValue;
            this.previousCounterValue = this.previousValue;

            this.animateDigits(
                oldValue,
                newValue,
                true,
                false,
                JackpotCounter.DigitAnimDuration * 10,
                0,
                "Back.easeOut",
                true,
                this.resetFinished,
            );
        } else {
            this.resetAnimCount++;
            const random = Math.random();
            const length = Math.pow(10, JackpotCounter.Length);
            const value = Math.round(random * length);

            const oldValue = this.formatValue(
                this.currentCounterValue > 0 ? this.currentCounterValue.toString() : "",
                false,
            );
            const newValue = this.formatValue(value.toString(), false);

            this.animateDigits(
                oldValue,
                newValue,
                true,
                false,
                JackpotCounter.DigitAnimDuration,
                0,
                "linear",
                true,
                this.resetCounter,
            );
            this.currentCounterValue = value;
        }
    }

    private resetFinished(): void {
        this.lastUpdateTime = Date.now();
        this.resetDigitTween();
        this.isCounting = false;
        this.refresh();
    }

    //******************************************************************************************************************
    //  WIN
    //******************************************************************************************************************

    private hasWin(): boolean {
        let result = false;
        if (this.currentData != null && this.currentData.lastWinAmount != null) {
            const currentLastWin =
                this.overridedLastWinValue > -1
                    ? this.overridedLastWinValue
                    : this.normalizeValue(this.currentData.lastWinAmount);
            const currentWinCount = this.currentData.winCount; //could be undefined during minigame spin

            if (this.lastWinValue > -1) {
                result = this.lastWinValue != currentLastWin;

                //second check if new win amount is same like last one
                //check only if it is not win from minigame (it has its own override)
                if (!result && this.lastWinCount > -1 && currentWinCount > -1 && !this.winFromMinigame) {
                    result = this.lastWinCount != currentWinCount;
                }
            }

            this.lastWinCount = currentWinCount;
            this.lastWinValue = currentLastWin;
            this.overridedLastWinValue = -1;
        }

        return result || this.winFromMinigame;
    }

    private initWinAnimation(): void {
        this.resetMainTween();

        if (this.digitTween != null) {
            this.digitTween.eventCallback("onComplete", this.startWinAnimation, [], this);
        } else {
            this.startWinAnimation();
        }
    }

    private startWinAnimation(): void {
        if (this.currentCounterValue != this.lastWinValue) {
            const oldValue = this.formatValue(this.currentCounterValue.toString(), true);
            const newValue = this.formatValue(this.lastWinValue.toString(), true);

            this.animateDigits(
                oldValue,
                newValue,
                true,
                true,
                JackpotCounter.DigitAnimDuration * 2,
                0,
                "linear",
                false,
                this.showWinAnimation,
            );
            this.previousCounterValue = this.currentCounterValue;
        } else {
            this.showWinAnimation();
        }

        this.winAnimationDelay.start(
            this.winFromMinigame ? JackpotCounter.WinFromMinigameAnimDuration : JackpotCounter.WinAnimDuration,
        );
    }

    private showWinAnimation(): void {
        this.forceWinValue();
        this.root.classList.add("jackpotWin");

        if (this.winFromMinigame) {
            this.root.classList.add("fromMinigame");
        }
    }

    private winAnimationFinished(): void {
        this.winFromMinigame = false;
        this.root.classList.remove("jackpotWin", "fromMinigame");
        this.reset();
    }

    //******************************************************************************************************************
    //  HELPER
    //******************************************************************************************************************

    public getRoot(): HTMLElement {
        return this.root;
    }

    public getHeight(): number {
        if (this.root.parentElement != null) {
            this.root.parentElement.classList.add("baseCounterHeight");
            this.root.parentElement.offsetWidth;

            let height = parseInt(window.getComputedStyle(this.root.parentElement).height);
            this.root.parentElement.classList.remove("baseCounterHeight");

            return height;
        }

        return 0;
    }

    private createElement(tag: string, className: string, rootElem?: HTMLElement): HTMLElement {
        let elem = document.createElement(tag);
        elem.setAttribute("class", className);

        if (rootElem) {
            rootElem.appendChild(elem);
        }

        return elem;
    }

    protected normalizeValue(value: number): number {
        let formattedValue = Languages.formatText(Languages.FormatMoney, value.toString());
        formattedValue = formattedValue.replace(".", "");

        value = parseInt(formattedValue);

        if (value >= Math.pow(10, JackpotCounter.Length)) {
            let normalizedValue = "";

            for (let i = 0; i < JackpotCounter.Length; i++) {
                normalizedValue = `9${normalizedValue}`;
            }

            return parseInt(normalizedValue);
        }

        return value;
    }

    protected formatValue(formattedValue: string, leading: boolean): string {
        if (formattedValue.length > JackpotCounter.Length) {
            formattedValue = formattedValue.substr(formattedValue.length - JackpotCounter.Length);
        } else {
            while (formattedValue.length < JackpotCounter.DecimalPlaces + 1) {
                formattedValue = `0${formattedValue}`;
            }

            while (formattedValue.length < JackpotCounter.Length) {
                formattedValue = `${leading ? JackpotCounter.LeadingZeroLetter : "0"}${formattedValue}`;
            }
        }

        return formattedValue;
    }

    protected resetMainTween(): void {
        if (this.mainTween != null) {
            this.mainTween.kill();
            this.mainTween.eventCallback("onComplete", null);
            this.mainTween.eventCallback("onUpdate", null);
            this.mainTween = null;
        }
    }

    protected resetDigitTween(onlyCallback: boolean = false): void {
        if (this.digitTween != null) {
            this.digitTween.eventCallback("onComplete", null);

            if (!onlyCallback) {
                this.digitTween.kill();
            }

            this.digitTween = null;
        }
    }

    protected resetDigitsToBasePosition(): void {
        const currentValue = this.formatValue(this.currentCounterValue.toString(), true);

        for (let i = 0; i < this.digits.length; i++) {
            this.setDigitValue(this.digits[i].to, currentValue.charAt(i), true);

            this.digits[i].from.setAttribute(
                "style",
                "transform: matrix(1, 0, 0, 1, 0, " + this.settings.height * -1 + ");",
            );
            this.digits[i].to.setAttribute("style", "transform: matrix(1, 0, 0, 1, 0, 0);");
        }
    }

    protected forceWinValue(): void {
        const currentValue = this.formatValue(this.lastWinValue.toString(), true);

        for (let i = 0; i < this.digits.length; i++) {
            this.setDigitValue(this.digits[i].to, currentValue.charAt(i), true);

            this.digits[i].from.setAttribute(
                "style",
                "transform: matrix(1, 0, 0, 1, 0, " + this.settings.height * -1 + ");",
            );
            this.digits[i].to.setAttribute("style", "transform: matrix(1, 0, 0, 1, 0, 0);");
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/Channels/Common/Jackpot/JackpotCounter.ts
 **/